import { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import './UserProfilePage.css';
import { useSession } from '../contexts/SessionContext';
import { useAuth } from '../contexts/AuthContext';
import { useGuidedTour } from '../contexts/GuidedTourContext';
import apiService from '../services/api';
import Toast from '../components/Toast';

interface LocationState {
  showSignInToast?: boolean;
}

const UserProfilePage = () => {
  const session = useSession();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { startTour } = useGuidedTour();
  const { userId, setUserId, resetUserId } = session;
  const [usage, setUsage] = useState<{
    user_type: string;
    max_chunks: number;
    current_chunks: number;
    remaining_chunks: number;
  } | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [hasSeenIndicator, setHasSeenIndicator] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);

  // Check if there's more content to scroll (only if user hasn't seen indicator before)
  const checkScrollable = useCallback(() => {
    if (hasSeenIndicator) {
      setShowScrollIndicator(false);
      return;
    }
    if (pageRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = pageRef.current;
      // Show indicator if there's more than 1px of content below
      const hasMoreContent = scrollHeight - scrollTop - clientHeight > 1;
      setShowScrollIndicator(hasMoreContent);

      // Once user starts scrolling, mark as seen
      if (scrollTop > 50) {
        setHasSeenIndicator(true);
        setShowScrollIndicator(false);
      }
    }
  }, [hasSeenIndicator]);

  // Check scrollability on mount and when content changes
  useEffect(() => {
    checkScrollable();
    // Re-check after a short delay to account for dynamic content loading
    const timer = setTimeout(checkScrollable, 500);
    return () => clearTimeout(timer);
  }, [checkScrollable, usage, auth.isAuthenticated]);

  // Fetch user usage/limits
  const fetchUsage = async (userIdToFetch: string) => {
    setLoadingUsage(true);
    try {
      const response = await apiService.getUserLimits(userIdToFetch);
      setUsage(response.data);
    } catch (error) {
      console.error('Error fetching usage:', error);
    } finally {
      setLoadingUsage(false);
    }
  };
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info') => {
    setToast({ message, type });
  }, []);

  const hideToast = useCallback(() => {
    setToast(null);
  }, []);

  // Show toast when session has expired
  useEffect(() => {
    if (auth.sessionExpired) {
      showToast('Your session has expired. Please sign in again.', 'info');
      auth.clearSessionExpired();
    }
  }, [auth, showToast]);

  // Show toast when redirected from protected route
  useEffect(() => {
    const state = location.state as LocationState | null;
    if (state?.showSignInToast && !auth.isAuthenticated) {
      showToast('Please sign in to access that page.', 'info');
      // Clear the state to avoid showing toast again on page refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, auth.isAuthenticated, showToast, navigate]);

  // When Google user signs in, automatically set userId to their Google sub
  // Note: userId is intentionally excluded from deps to prevent potential loops
  // The logic already guards against re-runs with the userId !== googleUserId check
  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      const googleUserId = auth.user.sub;
      if (userId !== googleUserId) {
        // Save the user to backend and update session
        apiService.saveUser(googleUserId, auth.user.email)
          .then(() => {
            setUserId(googleUserId);
            // Fetch usage after successful sign in
            fetchUsage(googleUserId);
            showToast('Signed in with Google successfully!', 'success');
          })
          .catch((error) => {
            console.error('Error saving Google user:', error);
            showToast('Failed to save Google user. Please try again.', 'error');
          });
      } else {
        // User is already set, just fetch usage
        fetchUsage(googleUserId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, auth.user, showToast]);

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    if (credentialResponse.credential) {
      try {
        await auth.signIn(credentialResponse.credential);
      } catch (error) {
        console.error('Sign in error:', error);
        showToast('Sign-in verification failed. Please try again.', 'error');
      }
    }
  };

  const handleGoogleError = () => {
    showToast('Google sign-in failed. Please try again.', 'error');
  };

  const handleSignOut = () => {
    auth.signOut();
    resetUserId();
    setUsage(null);
    showToast('Signed out successfully', 'success');
  };

  return (
    <div className="user-profile-page" ref={pageRef} onScroll={checkScrollable}>
      <h1>Settings</h1>

      {/* Google Sign-In Section */}
      <div className="profile-section google-section">
        <h2>Sign in with Google</h2>
        {auth.isAuthenticated && auth.user ? (
          <div className="google-user-info">
            <img
              src={auth.user.picture}
              alt={auth.user.name}
              className="google-avatar"
              referrerPolicy="no-referrer"
            />
            <div className="google-user-details">
              <span className="google-user-name">{auth.user.name}</span>
              <span className="google-user-email">{auth.user.email}</span>
            </div>
            <button onClick={handleSignOut} className="sign-out-button">
              Sign Out
            </button>
          </div>
        ) : (
          <div className="google-login-container">
            <p className="google-login-text">
              Please sign in with your Google account to access the app.
            </p>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              theme="outline"
              size="large"
              text="signin_with"
              shape="rectangular"
            />
          </div>
        )}
        {/* In-product privacy notification */}
        <div className="privacy-notice">
          <span className="privacy-notice-icon">🔒</span>
          <span className="privacy-notice-text">
            We only use your Google account for authentication. Your data is stored securely and never shared with third parties.{' '}
            <Link to="/privacy" className="privacy-notice-link">Learn more</Link>
          </span>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
          duration={3000}
        />
      )}

      {/* Usage Section */}
      {auth.isAuthenticated && (
        <div className="profile-section usage-section">
          <h2>Storage Usage</h2>
          {loadingUsage ? (
            <p>Loading usage...</p>
          ) : usage ? (
            <div className="usage-details">
              <div className="usage-stat">
                <span className="usage-label">Account Type:</span>
                <span className="usage-value">{usage.user_type}</span>
              </div>
              <div className="usage-stat">
                <span className="usage-label">Chunks Used:</span>
                <span className="usage-value">{usage.current_chunks.toLocaleString()} / {usage.max_chunks.toLocaleString()}</span>
              </div>
              <div className="usage-stat">
                <span className="usage-label">Remaining:</span>
                <span className="usage-value">{usage.remaining_chunks.toLocaleString()}</span>
              </div>
              <div className="usage-progress">
                <div
                  className="usage-progress-bar"
                  style={{
                    width: `${(usage.current_chunks / usage.max_chunks) * 100}%`,
                    backgroundColor: usage.remaining_chunks < usage.max_chunks * 0.1 ? '#f44336' : '#4caf50'
                  }}
                />
              </div>
              <p className="usage-percentage">
                {((usage.current_chunks / usage.max_chunks) * 100).toFixed(1)}% used
              </p>
              <button
                className="upgrade-button"
                onClick={() => navigate('/subscription')}
              >
                Change subscription
              </button>
            </div>
          ) : (
            <p>Unable to load usage information</p>
          )}
        </div>
      )}

      <div className="info-section">
        <div className="tutorial-link">
          <span>Learn how to use this app: </span>
          <a
            href="https://youtu.be/KI3ccK-yBqY"
            target="_blank"
            rel="noopener noreferrer"
            className="youtube-link"
          >
            Watch Tutorial Video
          </a>
        </div>

        {/* Guided Tour Section */}
        <div className="guided-tour-section">
          <button
            className="start-tour-button"
            onClick={startTour}
            disabled={!userId}
            title={!userId ? "Please log in to start the guided tour" : ""}
          >
            🎯 Start Guided Tour
          </button>
          <p className="tour-description">
            Take an interactive tour of all the app's features with step-by-step guidance.
            {!userId && <span className="login-required"> (Login required)</span>}
          </p>
        </div>

        {/* Keyboard Shortcuts Section */}
        <div className="keyboard-shortcuts-section">
          <h3>Keyboard Shortcuts</h3>
          <div className="shortcuts-list">
            <div className="shortcut-item">
              <kbd>→</kbd>
              <span className="shortcut-description">Select chunk</span>
            </div>
            <div className="shortcut-item">
              <kbd>←</kbd>
              <span className="shortcut-description">Discard chunk for session</span>
            </div>
            <div className="shortcut-item">
              <kbd>D</kbd> or <kbd>d</kbd>
              <span className="shortcut-description">Delete chunk permanently</span>
            </div>
            <div className="shortcut-item">
              <kbd>↑</kbd>
              <span className="shortcut-description">Navigate to previous chunk</span>
            </div>
            <div className="shortcut-item">
              <kbd>↓</kbd>
              <span className="shortcut-description">Navigate to next chunk</span>
            </div>
          </div>
        </div>

        <div className="info-row privacy-links">
          <button
            className="privacy-policy-button"
            onClick={() => setShowPrivacyModal(true)}
          >
            Privacy Summary
          </button>
          <Link to="/privacy" className="privacy-policy-link">
            Full Privacy Policy
          </Link>
        </div>
        <div className="info-row">
          <span>Contact: </span>
          <a href="mailto:andrewcbuensalida@gmail.com" className="email-link">
            andrewcbuensalida@gmail.com
          </a>
        </div>
        App version: 0.11.0
      </div>

      {showPrivacyModal && (
        <div className="modal-overlay" onClick={() => setShowPrivacyModal(false)}>
          <div className="modal privacy-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Privacy Policy</h3>
            <div className="privacy-content">
              <p><strong>Last updated:</strong> November 27, 2025</p>

              <h4>Information We Collect</h4>
              <p>When you sign in with Google, we collect your Google account email address and profile information to identify you within the app.</p>

              <h4>How We Use Your Information</h4>
              <p>We use your information solely to:</p>
              <ul>
                <li>Authenticate your identity</li>
                <li>Store and retrieve your data within the app</li>
                <li>Provide personalized app functionality</li>
              </ul>

              <h4>Data Storage</h4>
              <p>Your data is stored securely and is associated with your Google account identifier. We do not share your personal information with third parties.</p>

              <h4>Data Deletion</h4>
              <p>You may request deletion of your data by contacting us at <a href="mailto:andrewcbuensalida@gmail.com">andrewcbuensalida@gmail.com</a>.</p>

              <h4>Contact Us</h4>
              <p>If you have any questions about this Privacy Policy, please contact us at <a href="mailto:andrewcbuensalida@gmail.com">andrewcbuensalida@gmail.com</a>.</p>
            </div>
            <div className="modal-buttons">
              <button onClick={() => setShowPrivacyModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scroll indicator */}
      {showScrollIndicator && (
        <div className="scroll-indicator">
          <span className="scroll-arrow">↓</span>
        </div>
      )}
    </div>
  );
};

export default UserProfilePage;
